// Publish an element's own height as a CSS custom property on itself.
//
// Focus mode slides three bars away — the app header, the tune bar and the
// view switcher — and each has to take its space with it, or the sheet keeps a
// blank band where the bar used to be. A transform alone cannot do that: these
// are in normal flow (the header is sticky, which still occupies flow), so the
// slide has to be paired with a negative margin of exactly the bar's height.
//
// That height is not knowable in CSS and not safe to guess: the header wraps
// to two rows on a narrow phone and the tune bar's height follows its title.
// So it is measured, and re-measured on resize.

import { useCallback, useRef } from 'react';

/**
 * Returns a ref callback that keeps `property` on the element equal to its own
 * pixel height. The observer is disconnected when the ref detaches — React 18
 * ref callbacks cannot return a cleanup, so the previous one is torn down on
 * the next call (including the `null` call at unmount).
 *
 * `rootProperty` publishes the same height on `:root` as well, for the bars
 * that STACK: a sticky strip's `top` is the height of the bars above it, and a
 * custom property set on a sibling is invisible to it — only an ancestor's is
 * inherited. The root copy is cleared when the ref detaches, so a page without
 * the bar falls back to the stylesheet's default.
 */
export function useOwnHeight(property: string, rootProperty?: string) {
  const observed = useRef<ResizeObserver | null>(null);
  return useCallback(
    (node: HTMLElement | null) => {
      observed.current?.disconnect();
      observed.current = null;
      if (!node || typeof ResizeObserver === 'undefined') {
        if (rootProperty && typeof document !== 'undefined') {
          document.documentElement.style.removeProperty(rootProperty);
        }
        return;
      }
      const write = () => {
        // Exact, not rounded: a strip stacked under a 47.5px bar at a rounded
        // 48px left a half-pixel slit the sheet scrolled through.
        const px = `${node.getBoundingClientRect().height}px`;
        node.style.setProperty(property, px);
        if (rootProperty) document.documentElement.style.setProperty(rootProperty, px);
      };
      write();
      const observer = new ResizeObserver(write);
      observer.observe(node);
      observed.current = observer;
    },
    [property, rootProperty],
  );
}
